import Button from '@raydiant/raydial/components/Button';
import { Book, Plus, Trash } from '@raydiant/raydial/components/Icons';
import Link from '@raydiant/raydial/components/Link';
import { tv } from 'tailwind-variants';
import ProgressStepper, {
  ProgressStep,
} from '@raydiant/raydial/components/ProgressStepper';
import Text from '@raydiant/raydial/components/Text';
import TextField from '@raydiant/raydial/components/TextField';
import Modal from '@raydiant/raydial/components/Modal';
import Dialog, {
  DialogHeader,
  DialogHeading,
} from '@raydiant/raydial/components/Dialog';
import { useState } from 'react';
import { t } from 'i18next';

const stepStatus = (selectedStep: number, step: number) => {
  return (
    (step === selectedStep && 'selected') ||
    (selectedStep > step && 'complete') ||
    undefined
  );
};

export const QsrProgressStepperContainer = ({
  selectedStep,
}: {
  selectedStep: number;
}) => {
  return (
    <div className="px-8 mt-4 mb-7">
      <ProgressStepper>
        <ProgressStep step={1} variant={stepStatus(selectedStep, 1)}>
          Login
        </ProgressStep>
        <ProgressStep step={2} variant={stepStatus(selectedStep, 2)}>
          Find SiteUIDs
        </ProgressStep>
        <ProgressStep step={3} variant={stepStatus(selectedStep, 3)}>
          Enter SiteUIDs
        </ProgressStep>
      </ProgressStepper>
    </div>
  );
};

export const ProgressStepperContainer = ({
  selectedStep,
}: {
  selectedStep: number;
}) => {
  return (
    <div className="px-8 mt-4 mb-7">
      <ProgressStepper>
        <ProgressStep step={1} variant={stepStatus(selectedStep, 1)}>
          Login
        </ProgressStep>
        <ProgressStep step={2} variant={stepStatus(selectedStep, 2)}>
          Add {t('companyName')}
        </ProgressStep>
        <ProgressStep step={3} variant={stepStatus(selectedStep, 3)}>
          Check Email
        </ProgressStep>
        <ProgressStep step={4} variant={stepStatus(selectedStep, 4)}>
          Enter GUID's
        </ProgressStep>
      </ProgressStepper>
    </div>
  );
};

export const Header = ({
  heading,
  subheading,
}: {
  heading: string;
  subheading?: React.ReactNode;
}) => {
  return (
    <header className="flex items-center">
      <div className="grid gap-1 p-4 mr-auto">
        <Text as="h2" variant="subheader">
          {heading}
        </Text>
        <Text as="h3" className="text-gray-400 font-regular text-lg">
          {subheading}
        </Text>
      </div>
    </header>
  );
};

export const Footer = ({ children }: { children: React.ReactNode }) => {
  return <footer className="flex flex-wrap gap-6 py-4">{children}</footer>;
};

export type Site = {
  uid: string;
  name: string;
};

export type SiteUidTextFieldsProps = {
  sites: Array<Site>;
  onChange: (sites: Array<Site>) => void;
  disabledSites?: Array<Site>;
};

export type LocationGuidTextFieldsProps = {
  locationGuids: Array<string>;
  onChange: (locationGuids: Array<string>) => void;
  disabledGuids?: Array<string>;
};

const locationGuidTextFieldStyles = tv({
  base: 'text-gray-600 hover:text-red-600',
  variants: {
    topOffset: {
      true: 'relative top-[14.01px]',
    },
    bottomOffset: {
      true: 'relative bottom-[14.01px]',
    },
  },
});

export const SiteUidTextFields = (props: SiteUidTextFieldsProps) => {
  const { sites, onChange, disabledSites = [] } = props;

  const handleSiteUidChange = (value: string, index: number) => {
    const updatedSites = [...sites];
    updatedSites[index] = {
      ...updatedSites[index],
      uid: value,
    };
    onChange(updatedSites);
  };

  const handleNameChange = (value: string, index: number) => {
    const updatedSites = [...sites];
    updatedSites[index] = {
      ...updatedSites[index],
      name: value,
    };
    onChange(updatedSites);
  };

  const handleOnDeleteSite = (index: number) => {
    const updatedSites = [...sites];
    updatedSites.splice(index, 1);
    onChange(updatedSites);
  };

  const onAddSite = () => {
    onChange([...sites, { uid: '', name: '' }]);
  };

  return (
    <div className="mb-4">
      <div className="mb-4 flex flex-col gap-4">
        {sites.map((site, index) => {
          const isLastItem = sites.length - 1 === index;
          const isFirstItem = index === 0;
          const addTopOffset = isFirstItem && sites.length > 1;
          const addBottomOffset = isLastItem && sites.length > 1;
          const disabledSiteIds = disabledSites.map((site) => site.uid);

          return (
            <div className="flex flex-row gap-2" key={index}>
              <div className="flex items-center gap-2">
                <div className="flex-1 min-w-[300px]">
                  <TextField
                    label={isFirstItem ? 'SiteUID' : ''}
                    placeholder="Enter SiteUID"
                    description={
                      isLastItem && (
                        <Text slot="description">
                          eg: 6e12a27b-6439-42d1-a329-75387b644c73
                        </Text>
                      )
                    }
                    value={site.uid}
                    onChange={(value) => handleSiteUidChange(value, index)}
                    isDisabled={disabledSiteIds.includes(site.uid)}
                  />
                </div>
              </div>
              <div className="flex-1">
                <TextField
                  label={isFirstItem ? 'Site Name' : ''}
                  placeholder="Enter Site Name"
                  value={site.name}
                  onChange={(value) => handleNameChange(value, index)}
                  description={
                    isLastItem && <Text slot="description">eg: Site 1</Text>
                  }
                  isDisabled={disabledSiteIds.includes(site.uid)}
                />
              </div>
              <Button
                variant="unstyled"
                className={locationGuidTextFieldStyles({
                  topOffset: addTopOffset,
                  bottomOffset: addBottomOffset,
                })}
                onPress={() => handleOnDeleteSite(index)}
                isDisabled={disabledSiteIds.includes(site.uid)}
              >
                <Trash />
              </Button>
            </div>
          );
        })}
      </div>

      <Link icon={<Plus size={32} />} onPress={onAddSite}>
        <Text className="text-lg">Add another Site</Text>
      </Link>
    </div>
  );
};

export const LocationGuidTextFields = (props: LocationGuidTextFieldsProps) => {
  const { locationGuids, onChange, disabledGuids = [] } = props;

  const handleTextFieldChange = (guid: string, index: number) => {
    let updatedGuids = [...locationGuids];
    updatedGuids[index] = guid;

    onChange(updatedGuids);
  };

  const handleOnDeleteGuid = (index: number) => {
    let updatedGuids = [...locationGuids];
    updatedGuids.splice(index, 1);

    onChange(updatedGuids);
  };

  const onAddGuid = () => {
    onChange([...locationGuids, '']);
  };

  return (
    <div className="mb-4">
      <div className="mb-4 flex flex-col gap-1">
        {locationGuids.map((guid, index) => {
          const isLastItem = locationGuids.length - 1 === index;
          const isFirstItem = index === 0;
          const addTopOffset = isFirstItem && locationGuids.length > 1;
          const addBottomOffset = isLastItem && locationGuids.length > 1;

          return (
            <div className="flex items-center gap-2" key={index}>
              <div className="flex-1">
                <TextField
                  label={isFirstItem ? 'Location GUID' : ''}
                  placeholder="Enter Location GUID"
                  description={
                    isLastItem && (
                      <Text slot="description">
                        eg: 12kd9gvh-fi39-3ihc-ngi8-h85fwdtyh54ew{' '}
                        {/* TODO: Add learn more link */}
                        {/* <Link>Learn more</Link> */}
                      </Text>
                    )
                  }
                  value={guid}
                  onChange={(guid) => handleTextFieldChange(guid, index)}
                  isDisabled={disabledGuids.includes(guid)}
                />
              </div>
              <Button
                variant="unstyled"
                className={locationGuidTextFieldStyles({
                  topOffset: addTopOffset,
                  bottomOffset: addBottomOffset,
                })}
                onPress={() => {
                  handleOnDeleteGuid(index);
                }}
                isDisabled={locationGuids.length === 1}
              >
                <Trash />
              </Button>
            </div>
          );
        })}
      </div>

      <Link icon={<Plus size={32} />} onPress={onAddGuid}>
        <Text className="text-lg">Add another Toast Location GUID</Text>
      </Link>
    </div>
  );
};

interface IntegrationItemProps {
  heading: string;
  subheading?: React.ReactNode;
  icon: React.ReactNode;
  action: React.ReactNode;
}

export const IntegrationItem = ({
  heading,
  subheading,
  icon,
  action,
}: IntegrationItemProps) => {
  return (
    <div className="flex w-full p-4 items-center mb-4">
      <div className="mr-7 flex-shrink-0">{icon}</div>
      <div className="flex flex-col pr-4 mr-auto">
        <Text variant="smallSubheader" className="pb-1">
          {heading}
        </Text>
        <span className="text-gray-400 text-lg capitalize">{subheading}</span>
      </div>
      <div>{action}</div>
    </div>
  );
};

interface CatalogItemProps {
  heading: string;
  subheading?: string;
  action: React.ReactNode;
}

export const CatalogItem = ({
  heading,
  subheading,
  action,
}: CatalogItemProps) => {
  return (
    <div className="flex w-full p-4 items-center mb-4">
      <div className="mr-7 flex-shrink-0">
        <div className="size-16 bg-gray-200 rounded-lg flex items-center justify-center">
          <Book className="text-gray-500" />
        </div>
      </div>
      <div className="flex flex-col mr-auto pr-4">
        <Text variant="smallSubheader" className="pb-1">
          {heading}
        </Text>
        <span className="text-gray-400 text-lg">{subheading}</span>
      </div>
      <div>{action}</div>
    </div>
  );
};

type DeleteIntegrationConfirmationModalProps = {
  onClose: () => void;
  onDelete: () => Promise<void>;
  isOpen: boolean;
  connectionName: string;
};

export const DeleteIntegrationConfirmationModal = (
  props: DeleteIntegrationConfirmationModalProps,
) => {
  const { onClose, isOpen, onDelete, connectionName } = props;
  const [showUnableToDeleteModal, setShowUnableToDeleteModal] = useState(false);

  const handleDeleteIntegration = async (
    e: React.MouseEvent<HTMLFormElement>,
  ) => {
    e.preventDefault();
    try {
      await onDelete();
    } catch (error) {
      setShowUnableToDeleteModal(true);
      console.log('error here', error);
    }
  };

  const handleConfirmUnableToDelete = (
    e: React.MouseEvent<HTMLFormElement>,
  ) => {
    e.preventDefault();

    onClose();
    setShowUnableToDeleteModal(false);
  };

  const onCloseWarningModal = () => {
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onOpenChange={onCloseWarningModal}>
      <Dialog showCloseButton>
        {showUnableToDeleteModal ? (
          <form onSubmit={handleConfirmUnableToDelete}>
            <DialogHeader>
              <DialogHeading>Unable to delete integration</DialogHeading>
            </DialogHeader>

            <div className="px-4 mb-8 flex mt-4 flex-col">
              <Text className="mb-4">
                The integration you are trying to delete has one or more
                catalogs associated with it. Please delete all of the catalogs
                associated with this integration before proceeding.
              </Text>
            </div>

            <footer className="flex flex-wrap gap-6 pb-4 px-4">
              <Button size="large" className="flex-1" type="submit">
                Okay
              </Button>
            </footer>
          </form>
        ) : (
          <form onSubmit={handleDeleteIntegration}>
            <DialogHeader>
              <DialogHeading>Confirm delete integration</DialogHeading>
            </DialogHeader>

            <div className="px-4 mb-8 flex mt-4 flex-col">
              <Text className="mb-4">
                Are you sure you want to delete "{connectionName}" integration?
                This cannot be undone.
              </Text>
            </div>

            <footer className="flex flex-wrap gap-6 pb-4 px-4">
              <Button
                size="large"
                variant="secondary"
                className="flex-1"
                onPress={onCloseWarningModal}
              >
                Cancel
              </Button>
              <Button
                size="large"
                variant="caution"
                className="flex-1"
                type="submit"
              >
                Delete
              </Button>
            </footer>
          </form>
        )}
      </Dialog>
    </Modal>
  );
};

type DeleteCatalogConfirmationModalProps = {
  onClose: () => void;
  onDelete: () => Promise<void>;
  isOpen: boolean;
  catalogName: string;
};

export const DeleteCatalogConfirmationModal = (
  props: DeleteCatalogConfirmationModalProps,
) => {
  const { onClose, isOpen, onDelete, catalogName } = props;
  const [showUnableToDeleteModal, setShowUnableToDeleteModal] = useState(false);

  const handleDeleteCatalog = async (e: React.MouseEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      await onDelete();
    } catch (error) {
      setShowUnableToDeleteModal(true);
      console.log('error here', error);
    }
  };

  const handleConfirmUnableToDelete = (
    e: React.MouseEvent<HTMLFormElement>,
  ) => {
    e.preventDefault();

    onClose();
    setShowUnableToDeleteModal(false);
  };

  return (
    <Modal isOpen={isOpen} onOpenChange={onClose}>
      <Dialog showCloseButton>
        {showUnableToDeleteModal ? (
          <form onSubmit={handleConfirmUnableToDelete}>
            <DialogHeader>
              <DialogHeading>Unable to delete catalog</DialogHeading>
            </DialogHeader>

            <div className="px-4 mb-8 flex mt-4 flex-col">
              <Text className="mb-4">
                The catalog you are trying to delete has one or more
                presentations associated with it. Please delete all of the
                presentations associated with this catalog before proceeding.
              </Text>
            </div>

            <footer className="flex flex-wrap gap-6 pb-4 px-4">
              <Button size="large" className="flex-1" type="submit">
                Okay
              </Button>
            </footer>
          </form>
        ) : (
          <form onSubmit={handleDeleteCatalog}>
            <DialogHeader>
              <DialogHeading>Confirm delete catalog</DialogHeading>
            </DialogHeader>

            <div className="px-4 mb-8 flex mt-4 flex-col">
              <Text className="mb-4">
                Are you sure you want to delete "{catalogName}" catalog? This
                cannot be undone.
              </Text>
            </div>

            <footer className="flex flex-wrap gap-6 pb-4 px-4">
              <Button
                size="large"
                variant="secondary"
                className="flex-1"
                onPress={onClose}
              >
                Cancel
              </Button>
              <Button
                size="large"
                variant="caution"
                className="flex-1"
                type="submit"
              >
                Delete
              </Button>
            </footer>
          </form>
        )}
      </Dialog>
    </Modal>
  );
};
